import { Card, Pill, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CardContent, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { Button } from "@src/appV2/redesign/components/Button";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { format } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { utcToZonedTime } from "date-fns-tz";
import { useCallback } from "react";
import { useInView } from "react-intersection-observer";
import { useHistory } from "react-router-dom";

import { type PlacementCandidate } from "../../PlacementCandidate/types";
import { getDisputeBanPath } from "../paths";
import { DisputeRejectedAlert } from "./DisputeRejectedAlert";

interface RestrictedAccessBannerProps {
  placementCandidateBan: NonNullable<PlacementCandidate["mostRecentBan"]>;
  workerTimezone: string;
}

export function RestrictedAccessBanner(props: RestrictedAccessBannerProps) {
  const { placementCandidateBan, workerTimezone } = props;
  const history = useHistory();
  const { expiresAt, reason, id: banId, dispute } = placementCandidateBan;

  const disputeResolution =
    isDefined(dispute) && dispute.status === "rejected" && isDefined(dispute.resolution)
      ? dispute.resolution
      : undefined;

  const { ref } = useInView({
    triggerOnce: true,
    threshold: 0,
    onChange: (inView) => {
      if (!inView) {
        return;
      }

      logEvent(APP_V2_APP_EVENTS.PLACEMENT_RESTRICTED_ACCESS_BANNER_VIEWED, {
        placementCandidateBan,
      });
    },
  });

  const handleDisputeClick = useCallback(() => {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_RESTRICTED_ACCESS_BANNER_DISPUTE_CLICKED, {
      placementCandidateBan,
    });
    const disputePath = getDisputeBanPath(banId);

    history.push(disputePath);
  }, [placementCandidateBan, history, banId]);

  return (
    <Card
      ref={ref}
      sx={{
        px: 9,
        py: "90px",
        // This was using the tiers palette which is no longer in our theme
        // -> "theme.palette.tiers?.limited.background"
        // Please either switch to another color, or add this one to the theme under a different palette
        background: "linear-gradient(to bottom right, #FAECDE 0%, #F7D0D0 100%)",
        position: "relative",
      }}
    >
      <CardContent component={Stack} spacing={10} alignItems="center">
        <Stack spacing={5} sx={{ alignItems: "center", textAlign: "center" }}>
          {isDefined(expiresAt) && (
            <Pill
              iconType="clock-filled"
              label={`Lifting ${format(utcToZonedTime(expiresAt, workerTimezone), "MMMM d")}`}
              size="small"
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.4)",
              }}
            />
          )}

          <Text semibold variant="h4" color={(theme) => theme.palette.intent?.error.text}>
            Your account is restricted
          </Text>

          <Text variant="body2">{reason}</Text>
        </Stack>
        {isDefined(disputeResolution) && <DisputeRejectedAlert resolution={disputeResolution} />}
        <Button
          variant="outlined"
          disabled={isDefined(dispute)}
          sx={{ width: "fit-content", border: "none" }}
          onClick={handleDisputeClick}
        >
          {isDefined(dispute) ? "Disputed" : "Dispute Restriction"}
        </Button>
      </CardContent>
    </Card>
  );
}
